/**
 * Cart Engine — <cart-engine>
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * THE single cart writer (issue #20 cart-engine migration, supersedes #18's
 * Alpine.store('cart')-as-writer). Owns cart truth, fetch, LWW concurrency,
 * section-morph orchestration, and event dispatch. Ported from shopify-framework
 * (fw-cart-engine.js), adapted to feelrooty:
 *   - extends HTMLElement directly (feelrooty WC style; no shared Component base)
 *   - dispatches feelrooty's FROZEN store:cart:* / discount-* events (fw-events.js)
 *   - keeps feelrooty action behaviors: openDrawer semantics, _checkDiscountExists
 *     pre-validation, atomic changeSellingPlan (remove+re-add w/ rollback), i18n
 *   - reads window.Theme.routes / window.Theme.cartData (unchanged SSR contract)
 *
 * OWNERSHIP MODEL:
 *   <cart-engine> = truth + writer + dispatcher (this file)
 *      → dispatches store:cart:update { cart, source:'cart-engine', sections }
 *      → $store.cart (alpine-stores.js) is a READ-ONLY projection that syncs
 *      → the morph adapter forwards detail.sections to Theme.morphSections
 *   Templates act via $cart (Alpine magic → window.cartEngine).
 *
 * Two concurrency layers (see _postCart):
 *   1. per-key LWW (Theme.latestOnly) — collapses same-row spam to last write.
 *   2. mutation serializer (_mutationChain) — one POST in flight so full-cart
 *      snapshots apply in server order (the projection copies each snapshot).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

window.Theme = window.Theme || {};

const Logger = window.Theme.Logger || {
  ns: () => ({ debug() {}, info() {}, warn: console.warn.bind(console), error: console.error.bind(console) }),
};

// Fail LOUD on broken load order — the frozen event classes must exist.
const Events = window.Theme.events;
if (!Events) {
  throw new Error('[cart-engine] window.Theme.events missing — fw-events.js must load before cart-engine.js');
}
const { CartUpdateEvent, CartAddedEvent, CartErrorEvent, DiscountSuccessEvent, DiscountErrorEvent } = Events;

/**
 * The single cart-item matcher (by product_id OR variant_id). Published on
 * window.Theme so the $store.cart projection (Slice 3) shares the exact
 * semantics. Defined here because the engine owns cart truth.
 */
const matchCartItem = (items, productId) => {
  if (productId == null) return null;
  const idNum = Number(productId);
  return (items || []).find((i) => Number(i.product_id) === idNum || Number(i.variant_id) === idNum) || null;
};
window.Theme.matchCartItem = window.Theme.matchCartItem || matchCartItem;

const formatMoney = (cents) =>
  typeof window.Theme.formatMoney === 'function' ? window.Theme.formatMoney(cents) : '';

const routes = () => window.Theme.routes || {};

class CartEngine extends HTMLElement {
  // ─── State (canonical truth) ─────────────────────────────────────────────
  items = [];
  item_count = 0;
  total_price = 0;
  total_discount = 0;
  original_total_price = 0;
  currency = null;
  discount_codes = [];

  // UI state — projected to $store.cart for reactivity
  loading = false;
  itemLoading = {}; // { [key]: true }
  error = null;
  discountLoading = false;
  discountError = null;

  // Undo snapshot (last removed item)
  removedItem = null;

  // The morph adapter — engine owns it
  sectionAdapter = null;

  constructor() {
    super();
    this.log = Logger.ns('cart-engine');
  }

  connectedCallback() {
    // Cart-mutation serializer: one POST in flight at a time so full-cart
    // snapshots always apply in server order (the projection copies each).
    this._mutationChain = Promise.resolve();

    // SSR bootstrap from window.Theme.cartData (theme.liquid seed).
    const ssr = window.Theme.cartData || {};
    this.items = ssr.items || [];
    this.item_count = ssr.item_count || 0;
    this.total_price = ssr.total_price || 0;
    this.total_discount = ssr.total_discount || 0;
    this.original_total_price = ssr.original_total_price || 0;
    this.currency = ssr.currency || null;
    this.discount_codes = ssr.discount_codes || [];

    // Engine owns the morph adapter — its lifetime tracks the engine's DOM
    // consequences. Wired to feelrooty's frozen cart-update event name.
    const eventName = (Events.NAMES && Events.NAMES.cartUpdate) || 'store:cart:update';
    if (typeof window.Theme.createMorphAdapter === 'function') {
      this.sectionAdapter = window.Theme.createMorphAdapter({ eventName, sectionAttribute: 'data-cart-section' });
    } else {
      this.log.error('createMorphAdapter not available — section-morph-adapter.js missing?');
      this.sectionAdapter = { register() {}, unregister() {}, getIds: () => [] };
    }

    // Global handle for the $cart magic (Slice 3), tests, and non-Alpine callers.
    window.cartEngine = this;

    // NOTE (Slice 2 — dormant): deliberately NO initial cart:update dispatch.
    // The old Alpine.store('cart') is still the writer and seeds itself from
    // cartData; an init event here would wake cart-drawer's applyCart on load.
    // Slice 3 flips the store to a projection that self-seeds from getState().
    this.log.info('Connected', { item_count: this.item_count });
  }

  disconnectedCallback() {
    if (window.cartEngine === this) window.cartEngine = null;
  }

  // ─── Reads ────────────────────────────────────────────────────────────────

  getState() {
    return {
      items: this.items,
      item_count: this.item_count,
      total_price: this.total_price,
      total_discount: this.total_discount,
      original_total_price: this.original_total_price,
      currency: this.currency,
      discount_codes: this.discount_codes,
      loading: this.loading,
      itemLoading: this.itemLoading,
      error: this.error,
      discountLoading: this.discountLoading,
      discountError: this.discountError,
      removedItem: this.removedItem,
    };
  }

  findItem(productId) {
    return matchCartItem(this.items, productId);
  }
  hasItem(productId) {
    return !!this.findItem(productId);
  }
  quantityOf(productId) {
    const item = this.findItem(productId);
    return item ? item.quantity : 0;
  }

  // ─── Internal: dispatch / merge / fetch ──────────────────────────────────

  _dispatchUpdate(data, sections, sourceId = null) {
    try {
      document.dispatchEvent(
        new CartUpdateEvent({
          cart: this.getState(),
          source: 'cart-engine',
          sourceId,
          data: data || {},
          sections: sections || null,
        })
      );
    } catch (err) {
      this.log.error('Failed to dispatch store:cart:update', err);
    }
  }

  _dispatchError(error, sourceId = null, data = null) {
    try {
      document.dispatchEvent(new CartErrorEvent({ error, sourceId, data }));
    } catch (e) {
      this.log.error('Failed to dispatch store:cart:error', e);
    }
  }

  _merge(cart) {
    if (!cart) return;
    // Whitelist the fields feelrooty surfaces (the projection getters read
    // these). Server-only fields are intentionally omitted.
    ['items', 'item_count', 'total_price', 'total_discount', 'original_total_price', 'currency', 'discount_codes'].forEach((key) => {
      if (key in cart) this[key] = cart[key];
    });
  }

  async _fetchCart() {
    try {
      const res = await fetch(routes().cart || '/cart.js');
      if (!res.ok) {
        this.log.error('Failed to get cart', { status: res.status });
        return null;
      }
      return res.json();
    } catch (err) {
      this.log.error('Failed to get cart', err);
      return null;
    }
  }

  /**
   * POST a cart mutation with graceful empty-body handling. On HTTP error,
   * dispatches store:cart:error and throws a structured error.
   */
  async _sendCartRequest(endpoint, body, sourceId, signal) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok) {
      let err;
      try {
        err = await res.json();
      } catch {
        err = { description: `HTTP ${res.status} ${res.statusText || ''}`.trim(), status: res.status };
      }
      this._dispatchError(err, sourceId, body);
      throw err;
    }

    try {
      return await res.json();
    } catch {
      return {}; // 200 with empty body (e.g. /cart/update.js no-op) → no-op
    }
  }

  /**
   * Run a cart POST under per-key LWW + the mutation serializer, requesting the
   * registered morph sections. Returns { result, isLatest, sectionsForEvent }
   * or null (aborted by a newer same-key call).
   */
  async _postCart(endpoint, body, sourceId = null, lwwKey = 'cart') {
    const ids = this.sectionAdapter?.getIds?.() || [];
    if (ids.length > 0) body.sections = ids.join(',');

    const latestOnly = window.Theme.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.log.error('latestOnly not available — concurrency.js missing?');
      throw new Error('latestOnly not available');
    }

    const outcome = await latestOnly(lwwKey, async (signal) => {
      // Serialize all cart mutations behind one chain so snapshots apply in
      // server order. Re-check abort after our turn: a call superseded while
      // queued must not fire its (already-aborted) request.
      const turn = this._mutationChain;
      let release;
      this._mutationChain = new Promise((r) => { release = r; });
      try {
        await turn;
        if (signal.aborted) {
          const err = new Error('superseded while queued');
          err.name = 'AbortError';
          throw err;
        }
        return await this._sendCartRequest(endpoint, body, sourceId, signal);
      } finally {
        release();
      }
    });

    if (outcome === null) {
      this.log.debug('Request aborted (LWW)', { endpoint });
      return null;
    }

    const { result, isLatest } = outcome;
    // Only the latest response may carry sections (stale ones can't blat newer state).
    const sectionsForEvent = result && result.sections && isLatest ? result.sections : null;
    return { result, isLatest, sectionsForEvent };
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  /**
   * Add item(s) to the cart. `openCart` (default true) → CartAddedEvent
   * { openDrawer } so the drawer owns the open policy (mechanism-not-policy).
   */
  async add(options = {}) {
    const openCart = options.openCart !== false;
    const sourceId = options.sourceId || null;

    this.loading = true;
    this.error = null;
    this.removedItem = null;

    const items = options.items || [{
      id: options.id,
      quantity: options.quantity || 1,
      ...(options.properties && { properties: options.properties }),
      ...(options.selling_plan && { selling_plan: options.selling_plan }),
    }];

    try {
      const outcome = await this._postCart(routes().cartAdd || '/cart/add.js', { items }, sourceId, 'cart:add');
      if (!outcome) return null; // aborted (LWW) — newer call clears loading

      // add.js returns only the added lines; fetch canonical totals.
      const fullCart = await this._fetchCart();
      if (fullCart) this._merge(fullCart);
      this.loading = false;

      // addedKeys: the line keys add.js reports as added. The morph adapter
      // hands them to morphSections so only THOSE lines get the enter rise —
      // a line the morph re-inserts for any other reason stays instant.
      const addedKeys = (outcome.result?.items || []).map((i) => i.key).filter(Boolean);
      this._dispatchUpdate({ action: 'add', addedKeys }, outcome.sectionsForEvent, sourceId);
      // Announce the add; the drawer owns whether to open.
      document.dispatchEvent(new CartAddedEvent({ openDrawer: openCart }));
      this.log.info('Added to cart', { item_count: this.item_count });
      return outcome.result;
    } catch (err) {
      this.error = err?.message || err?.description || 'Add to cart failed';
      this.loading = false;
      this._dispatchUpdate({ action: 'add-error' }, null, sourceId);
      throw err;
    }
  }

  /** Add the current $store.selection (primary ATC path). */
  async addSelection(options = {}) {
    const selection = window.Alpine && Alpine.store('selection');
    if (!selection || !selection.variantId) {
      this.error = 'Please select a variant';
      this._dispatchUpdate({ action: 'add-error' }, null);
      return null;
    }
    if (!selection.available) {
      this.error = 'This item is not available';
      this._dispatchUpdate({ action: 'add-error' }, null);
      return null;
    }
    return this.add({
      id: selection.variantId,
      quantity: selection.quantity,
      selling_plan: selection.sellingPlanId,
      properties: selection.properties || undefined,
      openCart: options.openCart,
    });
  }

  /** Change a line's quantity by key. quantity 0 removes it server-side. */
  async changeItem(options = {}) {
    const key = options.key;
    if (!key || typeof key !== 'string' || key.trim() === '' || key.includes('$$')) {
      this.log.warn('Invalid line key, skipping changeItem', { key });
      this.error = 'Invalid cart item key';
      return null;
    }
    const sourceId = options.sourceId || null;
    const action = options.quantity === 0 ? 'remove' : 'change';

    this.itemLoading = { ...this.itemLoading, [key]: true };
    this.error = null;
    // Synchronous loading tick so the projection reflects itemLoading at once.
    this._dispatchUpdate({ action: `${action}-loading` }, null, sourceId);

    try {
      const outcome = await this._postCart(
        routes().cartChange || '/cart/change.js',
        { id: key.trim(), quantity: options.quantity },
        sourceId,
        `cart:line:${key}`
      );
      if (!outcome) return null; // aborted — newer call clears loading

      const { result, isLatest, sectionsForEvent } = outcome;
      if (result) this._merge(result);
      if (isLatest) this.itemLoading = {};
      this._dispatchUpdate({ action }, sectionsForEvent, sourceId);
      return result;
    } catch (err) {
      this.error = err?.message || err?.description || 'Update failed';
      const { [key]: _drop, ...rest } = this.itemLoading;
      this.itemLoading = rest;
      this._dispatchUpdate({ action: `${action}-error` }, null, sourceId);
      throw err;
    }
  }

  /** Remove a line by key. Snapshots for undoRemove() then sets quantity 0. */
  async removeItem(key) {
    const row = document.querySelector(`[data-key="${key}"]`);
    if (row) {
      row.style.setProperty('--row-height', `${row.offsetHeight}px`);
      row.classList.add('removing');
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    const item = (this.items || []).find((i) => i.key === key);
    if (item) {
      this.removedItem = {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
      };
    }
    return this.changeItem({ key, quantity: 0 });
  }

  increment(key) {
    const item = (this.items || []).find((i) => i.key === key);
    if (!item) return null;
    return this.changeItem({ key, quantity: item.quantity + 1 });
  }

  decrement(key) {
    const item = (this.items || []).find((i) => i.key === key);
    if (!item || item.quantity <= 1) return null;
    return this.changeItem({ key, quantity: item.quantity - 1 });
  }

  /** Re-add the most recently removed line (silent — drawer stays as-is). */
  async undoRemove() {
    if (!this.removedItem) return null;
    const { id, quantity, properties } = this.removedItem;
    this.removedItem = null;
    return this.add({ id, quantity, properties, openCart: false });
  }

  /**
   * Swap a line's selling plan — atomic remove + re-add with rollback (the Cart
   * API has no in-place plan change). The terminal step carries morph sections.
   */
  async changeSellingPlan(options = {}) {
    const key = options.key;
    const prev = (this.items || []).find((i) => i.key === key);
    if (!prev) {
      this.error = 'Cart item not found';
      return null;
    }

    const variantId = Number(prev.variant_id || prev.id);
    const quantity = prev.quantity;
    const nextPlan = options.sellingPlanId ? Number(options.sellingPlanId) : null;
    const prevPlan = prev.selling_plan_allocation?.selling_plan?.id || null;

    if ((nextPlan || null) === (prevPlan ? Number(prevPlan) : null)) {
      return null; // unchanged
    }

    const props = prev.properties && Object.keys(prev.properties).length > 0 ? prev.properties : null;
    this.itemLoading = { ...this.itemLoading, [key]: true };
    this.error = null;
    this._dispatchUpdate({ action: 'change-loading' }, null);

    try {
      // Step 1: remove the old line.
      await this._postCart(routes().cartChange || '/cart/change.js', { id: key, quantity: 0 }, null, `cart:line:${key}`);

      // Step 2: re-add with the new plan (its response carries the sections).
      let reAdd;
      try {
        reAdd = await this._postCart(routes().cartAdd || '/cart/add.js', {
          items: [{ id: variantId, quantity, ...(nextPlan && { selling_plan: nextPlan }), ...(props && { properties: props }) }],
        }, null, 'cart:add');
      } catch (addErr) {
        this.log.warn('Plan re-add failed, rolling back old line', { key });
        try {
          await this._postCart(routes().cartAdd || '/cart/add.js', {
            items: [{ id: variantId, quantity, ...(prevPlan && { selling_plan: Number(prevPlan) }), ...(props && { properties: props }) }],
          }, null, 'cart:add');
        } catch (rollbackErr) {
          this.log.error('Rollback failed — syncing to server state', { key });
        }
        throw addErr;
      }

      // Canonical totals + morph from the re-add's section payload.
      const fullCart = await this._fetchCart();
      if (fullCart) this._merge(fullCart);
      this.itemLoading = {};
      this._dispatchUpdate({ action: 'change-selling-plan' }, reAdd ? reAdd.sectionsForEvent : null);
      this.log.info('Selling plan changed', { key, nextPlan });
      return fullCart;
    } catch (err) {
      this.error = err?.message || err?.description || 'Plan change failed';
      // Sync to whatever the server holds (rollback or partial), morph via no-op.
      const sync = await this._postCart(routes().cartUpdate || '/cart/update.js', {}, null, 'cart');
      const full = await this._fetchCart();
      if (full) this._merge(full);
      const { [key]: _drop, ...rest } = this.itemLoading;
      this.itemLoading = rest;
      this._dispatchUpdate({ action: 'change-selling-plan-error' }, sync ? sync.sectionsForEvent : null);
      return null;
    }
  }

  // ─── Discount actions ────────────────────────────────────────────────────

  /** Undocumented-but-stable existence pre-check (fast-fail for typos). */
  async _checkDiscountExists(code) {
    try {
      const res = await fetch(`${routes().root || '/'}admin/discount_codes/lookup.json?code=${encodeURIComponent(code)}`, {
        credentials: 'same-origin',
      });
      return res.status === 200;
    } catch (err) {
      this.log.warn('Discount lookup failed, skipping pre-validation', { code });
      return true; // proceed anyway
    }
  }

  async applyDiscount(code) {
    if (!code || typeof code !== 'string') return false;
    code = code.trim().toUpperCase();
    if (!code) return false;

    this.discountLoading = true;
    this.discountError = null;
    this._dispatchUpdate({ action: 'discount-loading' }, null);
    const i18n = window.Theme?.i18n?.cart?.discount_code || {};

    const latestOnly = window.Theme.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.discountLoading = false;
      throw new Error('latestOnly not available');
    }

    try {
      const exists = await this._checkDiscountExists(code);
      if (!exists) {
        this.discountError = i18n.invalid || 'This discount code is not valid';
        document.dispatchEvent(new DiscountErrorEvent({ message: this.discountError }));
        return false;
      }

      const outcome = await latestOnly('discount', async (signal) => {
        await fetch(`${routes().root || '/'}discount/${encodeURIComponent(code)}`, {
          credentials: 'same-origin',
          redirect: 'follow',
          signal,
        });
        // No-op /cart/update.js pulls the updated cart + section payload.
        return this._postCart(routes().cartUpdate || '/cart/update.js', {});
      });

      if (outcome === null) return false; // aborted (LWW)

      const post = outcome.result;
      if (post) {
        if (post.result) this._merge(post.result);
        this._dispatchUpdate({ action: 'discount-apply' }, post.sectionsForEvent, null);
      }

      const wasApplied = (this.discount_codes || []).some(
        (dc) => dc.code && dc.code.toUpperCase() === code && dc.applicable !== false
      );
      if (!wasApplied) {
        this.discountError = i18n.not_applicable || 'This code cannot be applied to your cart';
        document.dispatchEvent(new DiscountErrorEvent({ message: this.discountError }));
      } else {
        document.dispatchEvent(new DiscountSuccessEvent());
      }
      return wasApplied;
    } catch (err) {
      this.log.error('Failed to apply discount', { code, error: err });
      this.discountError = i18n.error || 'Could not apply discount code';
      document.dispatchEvent(new DiscountErrorEvent({ message: this.discountError }));
      return false;
    } finally {
      this.discountLoading = false;
      this._dispatchUpdate({ action: 'discount-loading-cleared' }, null);
    }
  }

  async removeDiscount(codeToRemove) {
    if (!codeToRemove || typeof codeToRemove !== 'string') return false;
    codeToRemove = codeToRemove.trim().toUpperCase();

    this.discountLoading = true;
    this.discountError = null;
    this._dispatchUpdate({ action: 'discount-loading' }, null);

    const latestOnly = window.Theme.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.discountLoading = false;
      throw new Error('latestOnly not available');
    }

    try {
      const outcome = await latestOnly('discount', async (signal) => {
        const codesToKeep = (this.discount_codes || [])
          .filter((dc) => dc.applicable !== false && dc.code && dc.code.toUpperCase() !== codeToRemove)
          .map((dc) => dc.code);

        // Clear all, then re-apply the ones we keep.
        await fetch(routes().cartUpdate || '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
          signal,
        });
        for (const c of codesToKeep) {
          await fetch(`${routes().root || '/'}discount/${encodeURIComponent(c)}`, {
            credentials: 'same-origin',
            redirect: 'follow',
            signal,
          });
        }
        return this._postCart(routes().cartUpdate || '/cart/update.js', {});
      });

      if (outcome === null) return false; // aborted

      const post = outcome.result;
      if (post) {
        if (post.result) this._merge(post.result);
        this._dispatchUpdate({ action: 'discount-remove' }, post.sectionsForEvent, null);
      }
      this.log.info('Discount removed', { code: codeToRemove });
      return true;
    } catch (err) {
      this.log.error('Failed to remove discount', { code: codeToRemove, error: err });
      const i18n = window.Theme?.i18n?.cart?.discount_code || {};
      this.discountError = i18n.remove_failed || 'Could not remove discount code';
      return false;
    } finally {
      this.discountLoading = false;
      this._dispatchUpdate({ action: 'discount-loading-cleared' }, null);
    }
  }

  clearDiscountError() {
    this.discountError = null;
    this._dispatchUpdate({ action: 'discount-error-cleared' }, null);
  }

  /** Refresh canonical cart state from /cart.js. No section morph. */
  async refresh() {
    this.loading = true;
    this.error = null;
    const cart = await this._fetchCart();
    if (cart) this._merge(cart);
    this.loading = false;
    this.itemLoading = {};
    this._dispatchUpdate({ action: 'refresh' }, null);
    return cart;
  }

  formatMoney(cents) {
    return formatMoney(cents);
  }
}

// Register the singleton element.
if (!customElements.get('cart-engine')) {
  customElements.define('cart-engine', CartEngine);
  Logger.ns('cart-engine').info('Registered');
}

// Self-mount fallback: the standalone theme ships one <cart-engine> in
// layout/theme.liquid, but guarantee a mount if none exists once DOM is ready.
(function ensureEngineMounted() {
  const mount = () => {
    if (!document.querySelector('cart-engine')) {
      document.body.appendChild(document.createElement('cart-engine'));
      Logger.ns('cart-engine').info('Self-mounted (no static <cart-engine> in layout)');
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
})();

export { CartEngine };
